// Value checks (2026-10-28): values unusual for NGF's participants, values that do not fit together (BMI), and big changes
// since the last visit. Each must be fixed or ticked "Correct as entered" before the visit is marked reviewed or completed;
// the server asks for the same ones (calc-engine checks.ts via POST /api/assessments/value-checks).
import { useEffect, useState } from 'react';
import { post } from '../lib/api.ts';

export interface ValueFlag { code: string; field: string; message: string; key: string }
export interface ConfirmedCheck extends ValueFlag { byName: string | null; at: string }

export interface CheckRequest {
  participantId?: string | null; assessmentId?: string | null; dob?: string | null; sex?: string | null;
  assessedOn: string; measurements: unknown;
}

/** The flags for the values as they are now (asked again half a second after the last change). null: nothing to check. */
export function useValueChecks(req: CheckRequest | null): ValueFlag[] {
  const [flags, setFlags] = useState<ValueFlag[]>([]);
  const key = req ? JSON.stringify(req) : '';
  useEffect(() => {
    if (!req || !req.assessedOn) { setFlags([]); return; }
    let live = true;
    const t = setTimeout(() => {
      post<ValueFlag[]>('/api/assessments/value-checks', req).then((f) => { if (live) setFlags(f); }).catch(() => { /* values not valid yet */ });
    }, 500);
    return () => { live = false; clearTimeout(t); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return flags;
}

/** The yellow box: each flag with its "Correct as entered" tick. */
export function ValueChecksBox({ flags, confirmed, onChange }: { flags: ValueFlag[]; confirmed: Set<string>; onChange: (next: Set<string>) => void }) {
  if (!flags.length) return null;
  const open = flags.filter((f) => !confirmed.has(f.key)).length;
  const toggle = (k: string) => { const n = new Set(confirmed); if (n.has(k)) n.delete(k); else n.add(k); onChange(n); };
  return (
    <div className="alert warn value-checks">
      <b>Check {flags.length === 1 ? 'this value' : 'these values'} against the form</b>
      {open ? ` (${open} to confirm)` : ' (all confirmed)'}. Fix a typo, or tick it if the value really is right.
      <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
        {flags.map((f) => (
          <li key={f.key} style={{ marginBottom: 4 }}>
            {f.message}{' '}
            <label className="small" style={{ whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={confirmed.has(f.key)} onChange={() => toggle(f.key)} /> Correct as entered</label>
          </li>))}
      </ul>
    </div>
  );
}

/** Read-only: what was confirmed as right, and by whom (shown to the physician and on signed visits). */
export function ConfirmedChecks({ checks }: { checks: ConfirmedCheck[] | null | undefined }) {
  if (!checks?.length) return null;
  return (
    <div className="alert info small">
      <b>Unusual values confirmed as correct</b>
      <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.1rem' }}>
        {checks.map((c) => <li key={c.key}>{c.message} <span className="muted">Confirmed by {c.byName ?? 'NGF staff'}, {new Date(c.at).toLocaleDateString()}.</span></li>)}
      </ul>
    </div>
  );
}
